/**
 * TanStack Query hooks for the admin App Config surface.
 *
 * `useConfigList` — useQuery over `GET /admin/config`.
 * `usePutConfig`  — useMutation over `PUT /admin/config/{key}`; invalidates the
 *   `["admin","config"]` root on success so the list reflects the hot-reloaded
 *   value the server just published.
 * `useSaveGuestConfig` — the Guest access form's save (W3.1).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  configKeys,
  listConfig,
  putConfig,
  type AppConfigItem,
  type AppConfigListResponse,
  type AppConfigUpsertBody,
} from "@/features/admin/app-config/api";
import { flagKeys, updateFlag, type AdminFeatureFlag } from "@/features/admin/feature-flags/api";

export function useConfigList(): UseQueryResult<AppConfigListResponse, Error> {
  return useQuery<AppConfigListResponse, Error>({
    queryKey: configKeys.list,
    queryFn: listConfig,
    staleTime: 10_000,
  });
}

export function usePutConfig(): UseMutationResult<
  AppConfigItem,
  Error,
  { key: string; body: AppConfigUpsertBody }
> {
  const qc = useQueryClient();
  return useMutation<AppConfigItem, Error, { key: string; body: AppConfigUpsertBody }>({
    mutationFn: ({ key, body }) => putConfig(key, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: configKeys.list });
    },
  });
}

/** What the Guest access form writes — only the values that changed. */
export interface GuestConfigChanges {
  /** Flags to flip; `description` and `rules` go back unchanged. */
  flags: { flag: AdminFeatureFlag; enabled: boolean }[];
  /** app_config int keys, `value` as the canonical integer string. */
  numbers: { key: string; value: string; description: string | null }[];
}

/**
 * The Guest access form's save (W3.1, SCRUM-273): flag flips through
 * PUT /admin/feature-flags/{key}, numbers through PUT /admin/config/{key}, in
 * parallel. Both lists refetch whatever the outcome, so a partial failure
 * shows what actually stuck.
 */
export function useSaveGuestConfig(): UseMutationResult<void, Error, GuestConfigChanges> {
  const qc = useQueryClient();
  return useMutation<void, Error, GuestConfigChanges>({
    mutationFn: async ({ flags, numbers }) => {
      await Promise.all([
        ...flags.map(({ flag, enabled }) =>
          updateFlag(flag.key, { enabled, description: flag.description, rules: flag.rules }),
        ),
        ...numbers.map(({ key, value, description }) =>
          putConfig(key, { value, value_type: "int", description }),
        ),
      ]);
    },
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: configKeys.list }),
        qc.invalidateQueries({ queryKey: flagKeys.all }),
      ]);
    },
  });
}
